import React, { useState } from 'react';
import { 
  SearchCode, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle 
} from 'lucide-react';

interface MarketIntelligenceProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля конкурентної розвідки...",
  "ШІ-Дослідник ринку збирає дані з відкритих джерел та OSINT...",
  "Ринковий аналітик порівнює ціни та будує SWOT-матрицю...",
  "Стратегічний маркетолог формує рекомендації з позиціонування...",
  "Генерація підсумкового звіту конкурентної розвідки..."
];

const LOADING_STEPS_EN = [
  "Initializing competitor intelligence AI agents...",
  "AI market researcher scans open sources and gathers OSINT data...",
  "Market analyst compares pricing and structures SWOT matrices...",
  "Strategic marketer develops market positioning plans...",
  "Generating final competitor intelligence report..."
];

export default function MarketIntelligence({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: MarketIntelligenceProps) {
  const isUk = language === 'uk';
  const [marketSegment, setMarketSegment] = useState('');
  const [competitorList, setCompetitorList] = useState('');
  const [researchGoals, setResearchGoals] = useState(
    useEnterpriseContext ? (isUk ? 'Провести аналіз ринку та конкурентів на основі профілю та бази знань підприємства.' : 'Analyze market and competitors based on the company profile and knowledge base.') : ''
  );
  
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRun = async () => {
    if (!marketSegment.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Аналіз ринку та конкурентна розвідка",
          inputs: {
            market_segment: marketSegment,
            competitor_list: competitorList,
            research_goals: researchGoals || (isUk ? "Загальний аналіз ринку та конкурентів" : "General market and competitor analysis")
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу ринку.' : 'Failed to analyze market.'));
      }

            const data = await response.json();
      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report content.');
              }
              setReport(resText);
              setIsAuditing(false);
            } else if (statusData.status === 'failed') {
              setError(statusData.error || (isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsAuditing(false);
            } else {
              setTimeout(pollJob, 2000);
            }
          } catch (pollErr: any) {
            console.error(pollErr);
            setError(pollErr.message || (isUk ? 'Помилка опитування завдання.' : 'Polling error.'));
            setIsAuditing(false);
          }
        };
        setTimeout(pollJob, 1000);
      } else {
        setReport(data.report || data.result || '');
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = "Market_Intelligence_" + marketSegment.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <SearchCode className="w-6 h-6 text-red-500" />
              <span>{isUk ? "Конкурентна розвідка" : "Competitor Intelligence"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "OSINT-дослідження ринкових сегментів, аналіз ціноутворення конкурентів та SWOT-аналіз" : "OSINT analysis of market segments, competitor pricing strategies, and SWOT evaluations"}
            </p>
          </div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-red-500 animate-pulse" />
          <span className="text-[10px] text-red-500 font-bold uppercase tracking-wider">Multi-Agent AI</span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setMarketSegment("Мережі сонячних електростанцій та накопичувачів енергії (B2B/Home ESS) в Україні");
              setCompetitorList("EcoFlow Ukraine, Jackery, LogicPower, DEYE Ukraine, Victron Energy");
              setResearchGoals("Аналіз динаміки цін на гібридні інвертори 5-10 кВт у 2025/2026 роках, пошук незадоволеного попиту та пропозиція УТП для виходу нового бренду.");
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Аналіз ринку систем енергонезалежності)" : "⚡ Try 1-click Demo Sample (Energy Systems Market)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Ринковий сегмент / Галузь" : "Market segment / Industry"}
                </label>
                <input
                  type="text"
                  placeholder={isUk ? "Наприклад: Доставка здорової їжі у Києві" : "Example: Healthy food delivery in Kyiv"}
                  value={marketSegment}
                  onChange={(e) => setMarketSegment(e.target.value)}
                  className="w-full p-2.5 bg-slate-550/10 border border-slate-200 rounded-lg focus:border-red-500 focus:outline-none text-sm transition-colors text-slate-850"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Список конкурентів (необов'язково)" : "List of competitors (optional)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Вкажіть назви конкурентів, їхні веб-сайти або особливості..." 
                    : "Enter names of competitors, their websites, or details..."}
                  value={competitorList}
                  onChange={(e) => setCompetitorList(e.target.value)}
                  rows={6}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-red-500 focus:ring-1 focus:ring-red-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Цілі дослідження / Ключові питання (необов'язково)" : "Research goals / Key questions (optional)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Наприклад: Порівняти ціни, знайти слабкі сторони в логістиці, запропонувати УТП..." 
                    : "Example: Compare prices, find logistics bottlenecks, suggest USP..."}
                  value={researchGoals}
                  onChange={(e) => setResearchGoals(e.target.value)}
                  rows={4}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-red-500 focus:ring-1 focus:ring-red-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!marketSegment.trim()}
              className="w-full py-3 bg-red-500 hover:bg-red-650 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <SearchCode className="w-4 h-4" />
              <span>{isUk ? "Аналізувати ринок" : "Analyze Market"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-red-500" />
                <span>{isUk ? "Як це працює?" : "How it works?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-red-100 text-red-650 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "ШІ-скаутер здійснює пошук інформації про конкурентів з відкритих джерел." : "AI scout searches competitor information from public web pages."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-red-100 text-red-650 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Аналітик порівнює цінові пропозиції та будує SWOT-матрицю." : "Analyst matches price policies and compiles SWOT matrices."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-red-100 text-red-650 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "Маркетолог розробляє тактичні рекомендації для відбудови." : "Marketing specialist shapes tactical positioning advice."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-red-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-red-500 rounded-full animate-spin" />
            <SearchCode className="w-6 h-6 text-red-500" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Дослідження ринку аналітичними модулями..." : "Analyzing market by AI..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-red-50 p-1.5 rounded border border-red-100">
                <FileText className="w-4 h-4 text-red-600" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Маркетинговий звіт" : "Competitor Intelligence Report"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-550/30 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setMarketSegment('');
                setCompetitorList('');
                setResearchGoals('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Новий пошук" : "New Search"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
